/* ============================================================
 * N2T ship-week zone map — ZIP-5 lookup (CC3PL 2024 outbound)
 *
 * Maps every US ZIP-5 (~40,992 entries) to its UPS Ground transit-day
 * count from the CC3PL warehouse (60090 / Wheeling IL). Source data:
 * `shipping/2024 60090_Outbound.xlsx` (CC3PL's annual outbound rate
 * sheet). Rebuild via `scripts/build-zone-data.py` when CC3PL emails
 * a new sheet (~yearly cadence).
 *
 * The data file (`assets/ship-week-zones-data.json`, ~820KB raw /
 * ~80KB gzipped) is fetched LAZILY on first lookup — never inlined
 * on the PDP — so first-paint page weight is unchanged.
 *
 * `lookup()` returns a zone with effective `transit_days` after
 * applying N2T's air-upgrade policy from window.N2T_SHIP_WAREHOUSE:
 * anything > 3 ground days ships 2nd Day Air (2 days) instead, to
 * protect cold-chain on perishable seafood.
 *
 * US-only: N2T does not ship to Canada/Mexico/international.
 * ZIPs outside the US 5-digit format return null and the pill
 * renders the "ZIP not recognized" state.
 *
 * Async surface = `loadZones()` only. Callers should:
 *   await window.N2T_SHIP_ZONES.loadZones();
 *   const zone = window.N2T_SHIP_ZONES.lookup(zip);
 *
 * `lookup()` is intentionally synchronous: callers (PDP pill,
 * cart-stage component, tests) keep their existing sync contracts.
 * It returns null until loadZones() resolves; consumers re-render
 * after the load completes.
 * ============================================================ */
(function () {
  'use strict';

  // Idempotency guard. theme.liquid loads this file globally (for the
  // cart-stage component) AND snippets/ship-week-display.liquid loads it
  // for the PDP pill. Two <script> tags with the same URL = two IIFE
  // executions, which would wipe the _data cache between them. Skip the
  // second one — window.N2T_SHIP_ZONES is already wired up.
  if (typeof window !== 'undefined' && window.N2T_SHIP_ZONES) return;

  // Module-private cache. `_data` is null until loadZones() resolves;
  // `_loadPromise` is the in-flight fetch (idempotent across callers).
  let _data = null;
  let _loadPromise = null;

  function loadZones() {
    if (_data) return Promise.resolve(_data);
    if (_loadPromise) return _loadPromise;

    const url = (typeof window !== 'undefined') ? window.N2T_SHIP_ZONES_URL : null;
    if (!url) {
      _data = {};
      return Promise.resolve(_data);
    }

    _loadPromise = fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : {}; })
      .then(function (d) { _data = (d && typeof d === 'object') ? d : {}; return _data; })
      .catch(function () { _data = {}; return _data; });

    return _loadPromise;
  }

  function lookup(zip) {
    if (typeof zip !== 'string') return null;
    if (!isValidUSZip(zip)) return null;
    if (!_data) return null;

    const groundDays = _data[zip.trim().slice(0, 5)];
    if (typeof groundDays !== 'number') return null;

    // N2T's air-upgrade policy — read at call time so warehouse-config
    // changes propagate without reload. Behavior preserved from v1 stub.
    const cfg = (typeof window !== 'undefined' && window.N2T_SHIP_WAREHOUSE) || {};
    const threshold = (typeof cfg.air_upgrade_threshold_days === 'number')
      ? cfg.air_upgrade_threshold_days
      : Infinity;
    const airDays = (typeof cfg.air_transit_days === 'number')
      ? cfg.air_transit_days
      : 2;
    const upgradeToAir = groundDays > threshold;

    return {
      name: upgradeToAir ? ('Air-' + airDays + 'd') : ('Ground-' + groundDays + 'd'),
      ground_transit_days: groundDays,
      transit_days: upgradeToAir ? airDays : groundDays,
      service: upgradeToAir ? '2nd Day Air' : 'Ground',
    };
  }

  function isValidUSZip(input) {
    return typeof input === 'string' && /^\d{5}(-\d{4})?$/.test(input.trim());
  }

  // Test-only: lets the vitest suite seed the cache with a small fixture
  // without going through fetch(). No-op in production (callers should
  // never invoke this from theme code).
  function _setDataForTests(data) {
    _data = (data && typeof data === 'object') ? data : null;
    _loadPromise = null;
  }

  if (typeof window !== 'undefined') {
    window.N2T_SHIP_ZONES = {
      lookup: lookup,
      isValidUSZip: isValidUSZip,
      loadZones: loadZones,
      _setDataForTests: _setDataForTests,
    };
  }
})();
